import { useState } from "react";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Applications, CloseReport, Methods, Tasks } from "@/api/resources";
import { ApiError } from "@/api/client";
import type { MethodApplicationOut, MethodVersionOut, TerminalOutcome } from "@/api/types";
import { buildApplicationReport, type ReportDraft } from "@/lib/methodReport";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { SelectField, TextArea, TextField } from "@/components/ui/Field";
import { Sheet } from "@/components/ui/Sheet";
import { Spinner } from "@/components/ui/Spinner";
import { useToast } from "@/components/ui/Toast";

const OUTCOMES = [
  { value: "success", label: "Сработало" }, { value: "partial", label: "Помогло частично" },
  { value: "failed", label: "Не сработало" }, { value: "abandoned", label: "Применение остановлено" },
];

export function CloseTaskSheet({ taskId, version, statusId, completionNote, onClose }: {
  taskId: string; version: number; statusId: string; completionNote: string | null; onClose: () => void;
}) {
  const apps = useQuery({ queryKey: ["open-applications", taskId], queryFn: () => Applications.openForTask(taskId) });
  if (!apps.data || apps.isError) return <Sheet open onClose={onClose} title="Завершить задачу">
    {apps.isLoading ? <Spinner /> : <Card><p role="alert">{apps.error?.message || "Не удалось загрузить применения"}</p>
      <Button onClick={() => void apps.refetch()}>Повторить</Button></Card>}
  </Sheet>;
  return <TaskReportSheet taskId={taskId} applications={apps.data} taskVersion={version}
    statusId={statusId} completionNote={completionNote} onClose={onClose} />;
}

export function TaskReportSheet({ taskId, applications, taskVersion, statusId, completionNote, onClose }: {
  taskId: string; applications: MethodApplicationOut[]; taskVersion?: number; statusId?: string;
  completionNote?: string | null; onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [drafts, setDrafts] = useState<Record<string, ReportDraft>>({});
  const [note, setNote] = useState(completionNote ?? "");
  const journal = useQuery({ queryKey: ["journal", taskId], queryFn: () => Tasks.listJournal(taskId) });
  const methods = useQueries({ queries: applications.map((a) => ({
    queryKey: ["method", a.method_id], queryFn: () => Methods.get(a.method_id),
  })) });
  const applied = (i: number): MethodVersionOut | undefined => methods[i].data?.versions.find((v) => v.id === applications[i].version_id);
  const update = (id: string, values: Partial<ReportDraft>) => setDrafts((prev) => ({ ...prev, [id]: { ...prev[id], ...values } }));
  const submit = useMutation({
    mutationFn: () => CloseReport.submit(taskId, {
      close_task: Boolean(statusId), status_id: statusId, version: taskVersion,
      ...(statusId ? { completion_note_md: note.trim() || null } : {}),
      applications: applications.map((a, i) => buildApplicationReport(a, drafts[a.id] ?? {}, applied(i))),
    }),
    onSuccess: () => {
      for (const key of [["task", taskId], ["applications", taskId], ["open-applications", taskId],
        ["project-tasks"], ["tasks"], ["methods"], ["method"], ["suggestions"], ["memory-stats"]]) {
        void qc.invalidateQueries({ queryKey: key });
      }
      toast.success(statusId ? "Задача завершена, отчёт сохранён" : "Отчёт и уточнения сохранены");
      onClose();
    },
    onError: (error: Error) => toast.error(error.message),
  });
  const conflicted = submit.error instanceof ApiError && submit.error.status === 409;
  const reload = () => {
    for (const key of [["task", taskId], ["applications", taskId], ["open-applications", taskId], ["project-statuses"]]) {
      void qc.invalidateQueries({ queryKey: key });
    }
    onClose();
  };

  return <Sheet open onClose={() => { if (!submit.isPending) onClose(); }}
    title={statusId ? "Завершить задачу" : "Как прошло применение"}
    footer={<Button variant="strong" block loading={submit.isPending} loadingText="Сохраняем…"
      disabled={conflicted || applications.some((a) => !drafts[a.id]?.outcome)} onClick={() => submit.mutate()}>
      {statusId ? "Завершить и сохранить отчёт" : "Записать отчёт"}
    </Button>}>
    <fieldset disabled={submit.isPending} className="space-y-4">
      {statusId && <TextArea label="Итог задачи" value={note} onChange={(e) => setNote(e.target.value)} rows={3}
        hint="Что получилось и что стоит помнить в следующий раз" />}
      {applications.length > 0 && <p className="text-meta text-hint">Укажи исход каждого метода. Отчёты и новые версии сохранятся вместе.</p>}
      {applications.map((a, i) => {
        const d = drafts[a.id] ?? {};
        const version = applied(i);
        return <Card key={a.id} className="space-y-3">
          <h2 className="text-cell font-medium">{methods[i].data?.title ?? "Метод"}{version ? ` · v${version.number}` : ""}</h2>
          <SelectField label="Как сработал метод" value={d.outcome ?? ""} options={OUTCOMES}
            onChange={(v) => update(a.id, { outcome: (v || undefined) as TerminalOutcome | undefined })} />
          <TextArea label="Что получилось" value={d.result ?? ""} onChange={(e) => update(a.id, { result: e.target.value })} rows={2} />
          <TextArea label="Что пришлось изменить" value={d.adaptations ?? ""} onChange={(e) => update(a.id, { adaptations: e.target.value })} rows={2} />
          <TextArea label="Ограничения метода" value={d.limitations ?? ""} onChange={(e) => update(a.id, { limitations: e.target.value })} rows={2} />
          <fieldset className="space-y-1">
            <legend className="text-meta text-hint">Подтверждения из журнала задачи</legend>
            {journal.isLoading && <p className="text-meta text-hint">Загружаем журнал…</p>}
            {journal.isError && <Button variant="ghost" onClick={() => void journal.refetch()}>Повторить загрузку журнала</Button>}
            {journal.data?.length === 0 && <p className="text-meta text-hint">Журнал пуст. Отчёт можно записать без подтверждений.</p>}
            {(journal.data ?? []).map((entry) => <label key={entry.id} className="flex min-h-touch items-center gap-2">
              <input type="checkbox" className="h-4 w-4 accent-[var(--accent)]" checked={(d.evidence ?? []).includes(entry.id)}
                onChange={(e) => update(a.id, { evidence: e.target.checked ? [...(d.evidence ?? []), entry.id] : (d.evidence ?? []).filter((id) => id !== entry.id) })} />
              <span className="text-meta">{entry.summary ?? entry.body_md ?? "Запись"}</span>
            </label>)}
          </fieldset>
          <label className="flex min-h-touch items-center gap-2">
            <input type="checkbox" className="h-4 w-4 accent-[var(--accent)]" checked={d.asVersion ?? false}
              onChange={(e) => update(a.id, { asVersion: e.target.checked })} />
            <span className="text-cell">Сохранить уточнения как новую версию</span>
          </label>
          {d.asVersion && <>
            {version ? <TextArea label="Полный алгоритм новой версии" value={d.body ?? version.body_md}
              onChange={(e) => update(a.id, { body: e.target.value })} rows={7} />
              : <p className="text-meta text-hint">{methods[i].isError ? "Версия не загрузилась; исходные шаги сохранит сервер." : "Загружаем применённые шаги…"}</p>}
            <p className="text-meta text-hint">Условия и проверки сохранятся из применённой версии. Новые ограничения добавятся к прежним. Текущую версию можно выбрать на странице метода.</p>
          </>}
        </Card>;
      })}
      {submit.isError && <p role="alert" className="text-meta text-dstr">{submit.error.message}</p>}
      {conflicted && <div className="space-y-2">
        <p className="text-meta text-hint">Данные изменились. Перечитай задачу перед новым отчётом; введённый черновик будет сброшен.</p>
        <Button variant="secondary" onClick={reload}>Перечитать задачу</Button>
      </div>}
    </fieldset>
  </Sheet>;
}

export function ExtractTaskMethod({ taskId }: { taskId: string }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [summary, setSummary] = useState("");
  const navigate = useNavigate();
  const qc = useQueryClient();
  const save = useMutation({
    mutationFn: () => CloseReport.extractMethod(taskId, { title: title.trim() || null, summary: summary.trim() || null }),
    onSuccess: (method) => { void qc.invalidateQueries({ queryKey: ["methods"] }); navigate(`/knowledge/${method.id}`); },
  });
  return <>
    <Button variant="secondary" block onClick={() => setOpen(true)}>Сохранить решение как метод</Button>
    <Sheet open={open} onClose={() => { if (!save.isPending) setOpen(false); }} title="Метод из решения задачи"
      footer={<Button variant="strong" block loading={save.isPending} onClick={() => save.mutate()}>Создать черновик</Button>}>
      <div className="space-y-3">
        <p className="text-meta text-hint">Соберём алгоритм из журнала задачи. Записи, отмеченные ошибочными, не попадут в метод. Черновик можно дополнить перед использованием.</p>
        <TextField label="Название метода" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Можно оставить название задачи" maxLength={240} />
        <TextArea label="Когда пригодится" value={summary} onChange={(e) => setSummary(e.target.value)} rows={3} maxLength={240} />
        {save.isError && <p role="alert" className="text-meta text-dstr">{save.error.message}</p>}
      </div>
    </Sheet>
  </>;
}
